/* eslint-disable react-hooks/exhaustive-deps */
import { useAlert, useCompany } from '@orientaction/hooks';
import { BookTemplate, MajorTest, servicesService } from '@orientaction/services';
import { getFromLS } from '@orientaction/utils';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { getUserById } from '../../../services/user.service';
import { apiServiceNative } from '../../../utils/fetchNative';
import {
  buildDeletePrestationText,
  DELETE_PRESTATION_CONFIRM,
  DELETE_PRESTATION_SUCCESS,
  DELETE_PRESTATION_TITLE,
} from '../BeneficiaryProfile/constants';
import {
  getConsultantDisplayForService,
  getConsultantIdForService,
  getServiceName,
  getUserPrestations,
} from '../BeneficiaryProfile/utils';
import { fetchAssignableStaffUsers } from '../shared/assignableStaff';
import {
  BILAN_COMPETENCES_SERVICE_ID,
  CONSULTANT_ROLE_ID,
  OUTPLACEMENT_SERVICE_ID,
} from '../shared/constants';
import { buildServiceResourcesMapFromAssignments } from '../shared/serviceResourcesMap';
import {
  filterMinorCategoriesForStaff,
  getStaffRoleLabel,
  normalizeServiceList,
  readRole,
  resourceMatchesService,
  sortAssignableStaff,
  toId,
  toIdList,
  toResourceFromTemplate,
  unwrapCollection,
  unwrapRelation,
} from '../shared/utils';
import {
  CONSULTANT_REQUIRED,
  LEAVE_CONFIRM,
  LEAVE_TEXT,
  LEAVE_TITLE,
  SAVE_SUCCESS,
} from './constants';
import {
  ConsultantOption,
  ResourceItem,
  ResourceKind,
  ResourceStatus,
} from './types';

const qs = require('qs');

const MAJOR_RESULT_KEYS: Array<{ key: string; testId: number }> = [
  { key: 'meaningTestLastResult', testId: 1 },
  { key: 'needsTestLastResult', testId: 2 },
  { key: 'needsProTestLastResult', testId: 3 },
  { key: 'preferenceTestLastResult', testId: 4 },
  { key: 'harmonyTestLastResult', testId: 5 },
  { key: 'studyTestLastResult', testId: 6 },
  { key: 'preferenceJeuneLastResult', testId: 7 },
];

const serializeSelection = (
  consultantId: number | '',
  majorTests: ResourceItem[],
  minorTests: ResourceItem[],
  books: ResourceItem[],
  games: ResourceItem[],
  ebooks: ResourceItem[]
) =>
  JSON.stringify({
    consultantId: Number(consultantId) || '',
    majorTests: majorTests.map(item => item.id).sort((a, b) => a - b),
    minorTests: minorTests.map(item => item.id).sort((a, b) => a - b),
    books: books.map(item => item.id).sort((a, b) => a - b),
    games: games.map(item => item.id).sort((a, b) => a - b),
    ebooks: ebooks.map(item => item.id).sort((a, b) => a - b),
  });

const hydrateFromIds = (
  catalog: ResourceItem[],
  ids: number[],
  fallbacks: ResourceItem[] = []
): ResourceItem[] =>
  ids.map(
    id =>
      catalog.find(item => Number(item.id) === Number(id)) ||
      fallbacks.find(item => Number(item.id) === Number(id)) ||
      { id, label: `Ressource ${id}` }
  );

const toLabeledResources = (values: any[]): ResourceItem[] =>
  unwrapRelation(values)
    .map((item: any) => {
      const node = item?.attributes ? { id: item.id, ...item.attributes } : item;
      const id = Number(node?.id);
      if (!id) {
        return null;
      }
      return {
        id,
        label: node.title || node.name || `Ressource ${id}`,
      };
    })
    .filter((item: ResourceItem | null): item is ResourceItem => Boolean(item));

const getAssignmentSource = (user: any, targetId: number) => {
  const raw = unwrapRelation(user?.service_assignments).find((item: any) => {
    const source = item?.attributes ? { id: item.id, ...item.attributes } : item;
    const serviceRaw = source?.service?.data ?? source?.service;
    const serviceEntity = Array.isArray(serviceRaw) ? serviceRaw[0] : serviceRaw;
    return Number(serviceEntity?.id ?? serviceEntity) === Number(targetId);
  });
  if (!raw) {
    return null;
  }
  const source = raw?.attributes ? { id: raw.id, ...raw.attributes } : raw;
  const serviceRaw = source.service?.data ?? source.service;
  const serviceEntity = Array.isArray(serviceRaw) ? serviceRaw[0] : serviceRaw;
  const service = serviceEntity?.attributes
    ? { id: serviceEntity.id, ...serviceEntity.attributes }
    : serviceEntity;
  const hierarchyRaw = source.upper_hierarchy?.data ?? source.upper_hierarchy;
  const hierarchyEntity = Array.isArray(hierarchyRaw) ? hierarchyRaw[0] : hierarchyRaw;
  const upper_hierarchy = hierarchyEntity?.attributes
    ? { id: hierarchyEntity.id, ...hierarchyEntity.attributes }
    : hierarchyEntity;
  return { ...source, service, upper_hierarchy };
};

const mergeSelectedWithCatalog = (
  selected: ResourceItem[],
  catalog: ResourceItem[]
): ResourceItem[] =>
  selected.map(item => catalog.find(entry => Number(entry.id) === Number(item.id)) || item);

export const useEditPrestation = () => {
  const { id, serviceId: serviceIdParam } = useParams<{ id: string; serviceId: string }>();
  const serviceId = Number(serviceIdParam);
  const history = useHistory();
  const alert = useAlert();
  const token = getFromLS('userToken') || '';
  const userConnected = JSON.parse(getFromLS('userInfos') || '{}');
  const isConsultant = userConnected?.company_user_role?.id === CONSULTANT_ROLE_ID;
  const { companies } = useCompany();

  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [consultants, setConsultants] = useState<ConsultantOption[]>([]);
  const [currentConsultant, setCurrentConsultant] = useState<ConsultantOption | null>(null);
  const [consultantId, setConsultantId] = useState<number | ''>('');
  const [editingConsultant, setEditingConsultant] = useState(false);

  const [majorCatalog, setMajorCatalog] = useState<ResourceItem[]>([]);
  const [minorCatalog, setMinorCatalog] = useState<ResourceItem[]>([]);
  const [bookCatalog, setBookCatalog] = useState<ResourceItem[]>([]);
  const [gameCatalog, setGameCatalog] = useState<ResourceItem[]>([]);
  const [ebookCatalog, setEbookCatalog] = useState<ResourceItem[]>([]);

  const [majorTests, setMajorTests] = useState<ResourceItem[]>([]);
  const [minorTests, setMinorTests] = useState<ResourceItem[]>([]);
  const [books, setBooks] = useState<ResourceItem[]>([]);
  const [games, setGames] = useState<ResourceItem[]>([]);
  const [ebooks, setEbooks] = useState<ResourceItem[]>([]);

  const [statusById, setStatusById] = useState<Record<number, ResourceStatus>>({});
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [consultantsReady, setConsultantsReady] = useState(false);
  const snapshotRef = useRef('');
  const allowLeaveRef = useRef(false);
  const pendingPathRef = useRef('');
  const hydratedRef = useRef(false);
  const assignmentIdsRef = useRef<{
    major_test_ids: number[];
    tests_to_do: number[];
    book_template_ids: number[];
    authorized_games: number[];
    authorized_ebooks: number[];
  }>({
    major_test_ids: [],
    tests_to_do: [],
    book_template_ids: [],
    authorized_games: [],
    authorized_ebooks: [],
  });
  const assignmentLabelsRef = useRef<{
    major_test_ids: ResourceItem[];
    tests_to_do: ResourceItem[];
    book_template_ids: ResourceItem[];
    authorized_games: ResourceItem[];
    authorized_ebooks: ResourceItem[];
  }>({
    major_test_ids: [],
    tests_to_do: [],
    book_template_ids: [],
    authorized_games: [],
    authorized_ebooks: [],
  });

  const companyAlias = useMemo(() => {
    const companyId = Number(
      user?.company?.id || user?.company?.data?.id || userConnected?.company?.id
    );
    return (companies || []).find((item: any) => Number(item.id) === companyId)?.attributes
      ?.companyUserRoleAlias;
  }, [companies, user, userConnected?.company?.id]);

  const consultantsForService = useMemo(() => {
    let list = consultants;
    if (serviceId) {
      const hasServiceData = consultants.some(item => item.allowedServices.length > 0);
      if (hasServiceData) {
        list = consultants.filter(item =>
          item.allowedServices.some(service => Number(service.id) === Number(serviceId))
        );
      }
    }
    if (
      currentConsultant?.id &&
      !list.some(item => Number(item.id) === Number(currentConsultant.id))
    ) {
      return sortAssignableStaff([currentConsultant, ...list]);
    }
    return list;
  }, [consultants, serviceId, currentConsultant]);

  const selectedConsultant = useMemo(() => {
    if (!consultantId) {
      return null;
    }
    return (
      consultants.find(item => Number(item.id) === Number(consultantId)) ||
      (Number(currentConsultant?.id) === Number(consultantId) ? currentConsultant : null)
    );
  }, [consultants, currentConsultant, consultantId]);

  const prestationName = useMemo(() => {
    const assignment = getAssignmentSource(user, serviceId);
    if (assignment?.service) {
      return getServiceName(assignment.service);
    }
    const service = getUserPrestations(user).find(item => Number(item.id) === serviceId);
    return service ? getServiceName(service) : `Prestation ${serviceId || ''}`;
  }, [user, serviceId]);

  const consultantName =
    `${selectedConsultant?.firstname || ''} ${selectedConsultant?.lastname || ''}`.trim() ||
    getConsultantDisplayForService(user, serviceId);

  const dirty = useMemo(() => {
    if (!hydrated) {
      return false;
    }
    return (
      serializeSelection(consultantId, majorTests, minorTests, books, games, ebooks) !==
      snapshotRef.current
    );
  }, [hydrated, consultantId, majorTests, minorTests, books, games, ebooks]);

  const applyStatus = (items: ResourceItem[], defaultTodo: boolean) =>
    items.map(item => {
      const status = statusById[item.id] || (defaultTodo ? item.status || 'todo' : item.status);
      return status ? { ...item, status } : item;
    });

  const beneficiaryProfilePath = `/admin/users-resultats/beneficiaire/${id}?tab=prestations`;

  const goToBeneficiaries = () => {
    history.push(beneficiaryProfilePath);
  };

  const requestLeave = (path = beneficiaryProfilePath) => {
    if (!dirty) {
      history.push(path);
      return;
    }
    pendingPathRef.current = path;
    setLeaveOpen(true);
  };

  const confirmLeave = () => {
    allowLeaveRef.current = true;
    setLeaveOpen(false);
    const path = pendingPathRef.current || beneficiaryProfilePath;
    pendingPathRef.current = '';
    history.push(path);
  };

  useEffect(() => {
    if (!dirty) {
      return undefined;
    }
    const unblock = history.block(location => {
      if (allowLeaveRef.current) {
        return undefined;
      }
      pendingPathRef.current = `${location.pathname}${location.search || ''}`;
      setLeaveOpen(true);
      return false;
    });
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      unblock();
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [dirty, history]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const populateParams = qs.stringify(
          {
            populate: {
              service: { fields: ['id', 'name'] },
              services: { fields: ['id', 'name'] },
              upper_hierarchy: { fields: ['id', 'firstname', 'lastname', 'photoUri'] },
              service_assignments: {
                populate: {
                  service: { fields: ['id', 'name'] },
                  upper_hierarchy: { fields: ['id', 'firstname', 'lastname', 'photoUri'] },
                  tests_to_do: { fields: ['id', 'title', 'name'] },
                  major_tests: { fields: ['id', 'name'] },
                  book_templates: { fields: ['id', 'name'] },
                  games: { fields: ['id', 'title'] },
                  ebooks: { fields: ['id', 'title'] },
                },
              },
            },
          },
          { encodeValuesOnly: true }
        );
        const [response, contextRes] = await Promise.all([
          getUserById({ token, userId: id }),
          apiServiceNative.get(`/api/users/${id}?${populateParams}`).catch(() => null),
        ]);
        const next = response?.data || response;
        const context = contextRes?.data || contextRes || {};
        if (cancelled) {
          return;
        }
        const merged = {
          ...next,
          service: context.service ?? next?.service,
          services: context.services ?? next?.services,
          upper_hierarchy: context.upper_hierarchy ?? next?.upper_hierarchy,
          service_assignments: context.service_assignments ?? next?.service_assignments,
          service_resources_map: context.service_resources_map ?? next?.service_resources_map,
        };
        setUser(merged);
        const map = buildServiceResourcesMapFromAssignments(merged?.service_assignments);
        const jsonMap = merged?.service_resources_map || {};
        const entry = map[String(serviceId)] || jsonMap[String(serviceId)] || {};
        const assignment = getAssignmentSource(merged, serviceId);
        assignmentIdsRef.current = {
          major_test_ids: toIdList(entry.major_test_ids),
          tests_to_do: toIdList(entry.tests_to_do),
          book_template_ids: toIdList(entry.book_template_ids),
          authorized_games: toIdList(entry.authorized_games),
          authorized_ebooks: toIdList(entry.authorized_ebooks),
        };
        assignmentLabelsRef.current = {
          major_test_ids: toLabeledResources(assignment?.major_tests || assignment?.major_test_ids),
          tests_to_do: toLabeledResources(assignment?.tests_to_do),
          book_template_ids: toLabeledResources(
            assignment?.book_templates || assignment?.book_template_ids
          ),
          authorized_games: toLabeledResources(assignment?.games || assignment?.authorized_games),
          authorized_ebooks: toLabeledResources(assignment?.ebooks || assignment?.authorized_ebooks),
        };
        const hierarchyId = getConsultantIdForService(merged, serviceId);
        if (hierarchyId) {
          setConsultantId(Number(hierarchyId));
          const owner = assignment?.upper_hierarchy;
          setCurrentConsultant({
            id: Number(owner?.id || hierarchyId),
            firstname: owner?.firstname || '',
            lastname: owner?.lastname || '',
            photoUri: owner?.photoUri,
            allowedServices: [],
            categoryTestIds: [],
          });
        }

        try {
          const results = await MajorTest.getUserAllTestLatestResult(token, Number(id));
          const payload = results?.data?.data || results?.data || results;
          const nextStatus: Record<number, ResourceStatus> = {};
          MAJOR_RESULT_KEYS.forEach(config => {
            if (payload?.[config.key]?.id) {
              nextStatus[config.testId] = 'done';
            }
          });
          unwrapCollection(payload?.customTestResults).forEach((result: any) => {
            const testId = Number(result?.test?.id || result?.test);
            if (testId) {
              nextStatus[testId] = 'done';
            }
          });
          if (!cancelled) {
            setStatusById(nextStatus);
          }
        } catch {
          // Status tags stay optional if the results endpoint is unavailable.
        }

        try {
          const responseUsers = await apiServiceNative.get(
            `/api/response-users?filters[users_permissions_users][id][$eq]=${id}&sort[1]=createdAt%3Adesc&populate=test&pagination[pageSize]=200`
          );
          const progress: Record<number, ResourceStatus> = {};
          unwrapCollection(
            responseUsers?.data?.data || responseUsers?.data || responseUsers
          ).forEach((row: any) => {
            const attrs = row?.attributes || row;
            const testId = Number(
              attrs?.test?.id || attrs?.test?.data?.id || row?.test?.id || row?.test?.data?.id
            );
            const state = attrs?.state || row?.state;
            if (!testId || progress[testId] === 'done') {
              return;
            }
            if (state === 'over') {
              progress[testId] = 'done';
            } else if (state === 'started') {
              progress[testId] = 'progress';
            }
          });
          if (!cancelled) {
            setStatusById(current => ({ ...progress, ...current }));
          }
        } catch {
          // Keep "Pas fait" when progress cannot be loaded.
        }
      } catch (error) {
        if (!cancelled) {
          alert.openSimpleAlert();
          history.push('/admin/users-resultats');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id, serviceId, token]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }
    let cancelled = false;
    const companyId =
      Number(user?.company?.id || user?.company?.data?.id || userConnected?.company?.id) || 1;

    const mapConsultant = (item: any): ConsultantOption => {
      const role = readRole(item.company_user_role) || undefined;
      return {
        id: Number(item.id),
        firstname: item.firstname || '',
        lastname: item.lastname || '',
        photoUri: item.photoUri,
        company_user_role: role,
        roleLabel: getStaffRoleLabel(role, companyAlias),
        allowedServices: normalizeServiceList(item.allowed_services),
        categoryTestIds: (item.category_tests || []).map((category: any) => Number(category.id)),
      };
    };

    const loadConsultants = async () => {
      try {
        const users = await fetchAssignableStaffUsers({
          companyId,
          consultantOnly: isConsultant,
          allowedServiceId: serviceId || undefined,
          fields: ['id', 'firstname', 'lastname', 'photoUri'],
        });
        if (cancelled) {
          return;
        }
        const list: ConsultantOption[] = sortAssignableStaff(users.map(mapConsultant));
        setConsultants(list);

        const ownerId = getConsultantIdForService(user, serviceId) || consultantId;
        const fromList = list.find(item => Number(item.id) === Number(ownerId));
        if (fromList) {
          setCurrentConsultant(fromList);
        } else if (ownerId) {
          try {
            const ownerParams = qs.stringify(
              {
                fields: ['id', 'firstname', 'lastname', 'photoUri'],
                populate: {
                  allowed_services: { fields: ['id', 'name'] },
                  company_user_role: { fields: ['id', 'name'] },
                  category_tests: { fields: ['id'] },
                },
              },
              { encodeValuesOnly: true }
            );
            const ownerRes = await apiServiceNative.get(`/api/users/${ownerId}?${ownerParams}`);
            const owner = ownerRes?.data || ownerRes;
            if (!cancelled && owner?.id) {
              setCurrentConsultant(mapConsultant(owner));
            }
          } catch {
            // Keep the journal identity if the consultant is no longer listable.
          }
        }
      } catch (error) {
        if (!cancelled) {
          alert.openSimpleAlert();
        }
      } finally {
        if (!cancelled) {
          setConsultantsReady(true);
        }
      }
    };

    loadConsultants();
    return () => {
      cancelled = true;
    };
  }, [user, serviceId, isConsultant, companyAlias]);

  useEffect(() => {
    if (!consultantId || !serviceId || !consultantsReady) {
      return undefined;
    }
    let cancelled = false;

    const loadResourceCatalogs = async () => {
      setLoadingCatalogs(true);
      try {
        const consultant =
          consultants.find(item => Number(item.id) === Number(consultantId)) ||
          (Number(currentConsultant?.id) === Number(consultantId) ? currentConsultant : null);
        const populate = {
          major_tests_templates: { fields: ['id', 'name', 'query'], sort: ['id:asc'] },
          book_templates: {
            fields: ['id', 'name', 'query', 'route', 'HourRead'],
            sort: ['id:asc'],
          },
        };
        const skipMinors = Number(serviceId) === OUTPLACEMENT_SERVICE_ID;
        let categoriesPromise: Promise<any> = Promise.resolve({ data: [] });
        if (!skipMinors) {
          const stored = sessionStorage.getItem('categories-test');
          if (stored) {
            try {
              categoriesPromise = Promise.resolve(JSON.parse(stored));
            } catch {
              categoriesPromise = apiServiceNative.get('/api/category-tests?populate=tests');
            }
          } else {
            categoriesPromise = apiServiceNative.get('/api/category-tests?populate=tests');
          }
        }
        const [serviceRes, gamesRes, ebooksRes, categoriesRes] = await Promise.all([
          servicesService.getServiceById(token, serviceId, ['id', 'name'], {}, populate),
          apiServiceNative.get('/api/game-list'),
          apiServiceNative.get('/api/ebook-list'),
          categoriesPromise,
        ]);
        if (cancelled) {
          return;
        }
        const servicePayload = serviceRes?.data?.data || serviceRes?.data || serviceRes;
        const serviceAttrs = servicePayload?.attributes || servicePayload;
        const nextMajor = unwrapRelation(serviceAttrs?.major_tests_templates).map((item: any) =>
          toResourceFromTemplate(item)
        );
        let bookRaw = unwrapRelation(serviceAttrs?.book_templates);
        if (!bookRaw.length && Number(serviceId) === BILAN_COMPETENCES_SERVICE_ID) {
          const booksRes = await BookTemplate.getUserBooks(token, { userId: 0 });
          bookRaw = booksRes?.data?.books || [];
        }
        if (cancelled) {
          return;
        }
        const nextBooks = bookRaw.map((item: any) => toResourceFromTemplate(item));
        const nextGames = unwrapCollection(gamesRes?.data || gamesRes)
          .filter((game: any) => game.status !== 'draft')
          .filter((game: any) => resourceMatchesService(game, Number(serviceId)))
          .map((game: any) => ({
            id: Number(game.id),
            label: game.title || game.name || `Serious game ${game.id}`,
          }));
        const nextEbooks = unwrapCollection(ebooksRes?.data || ebooksRes)
          .filter((ebook: any) => ebook.status !== 'draft')
          .filter((ebook: any) => resourceMatchesService(ebook, Number(serviceId)))
          .map((ebook: any) => ({
            id: Number(ebook.id),
            label: ebook.title || ebook.name || `Ebook ${ebook.id}`,
          }));
        const categories = filterMinorCategoriesForStaff(
          unwrapCollection(categoriesRes?.data || categoriesRes),
          {
            company_user_role: consultant?.company_user_role,
            categoryIds: consultant?.categoryTestIds,
          },
          { showAllWhenConsultantHasNone: true }
        ).sort(
            (a: any, b: any) =>
              Number(a.attributes?.priority || a.priority || 0) -
              Number(b.attributes?.priority || b.priority || 0)
          );
        const nextMinors: ResourceItem[] = [];
        categories.forEach((category: any) => {
          const group = category.attributes?.name || category.name || '';
          const groupSubtitle = category.attributes?.subtitle || category.subtitle || '';
          const groupId = toId(category);
          unwrapRelation(category.attributes?.tests || category.tests)
            .filter((test: any) => {
              const attrs = test.attributes || test;
              return !attrs.isArchived && !attrs.isDesabled;
            })
            .forEach((test: any) => {
              nextMinors.push(toResourceFromTemplate(test, group, groupSubtitle, groupId));
            });
        });
        setMajorCatalog(nextMajor);
        setBookCatalog(nextBooks);
        setGameCatalog(nextGames);
        setEbookCatalog(nextEbooks);
        setMinorCatalog(nextMinors);

        if (!hydratedRef.current) {
          const assigned = assignmentIdsRef.current;
          const labels = assignmentLabelsRef.current;
          const nextMajorSelected = hydrateFromIds(
            nextMajor,
            assigned.major_test_ids,
            labels.major_test_ids
          );
          const nextMinorSelected = hydrateFromIds(
            nextMinors,
            assigned.tests_to_do,
            labels.tests_to_do
          );
          const nextBookSelected = hydrateFromIds(
            nextBooks,
            assigned.book_template_ids,
            labels.book_template_ids
          );
          const nextGameSelected = hydrateFromIds(
            nextGames,
            assigned.authorized_games,
            labels.authorized_games
          );
          const nextEbookSelected = hydrateFromIds(
            nextEbooks,
            assigned.authorized_ebooks,
            labels.authorized_ebooks
          );
          setMajorTests(nextMajorSelected);
          setMinorTests(nextMinorSelected);
          setBooks(nextBookSelected);
          setGames(nextGameSelected);
          setEbooks(nextEbookSelected);
          snapshotRef.current = serializeSelection(
            consultantId,
            nextMajorSelected,
            nextMinorSelected,
            nextBookSelected,
            nextGameSelected,
            nextEbookSelected
          );
          hydratedRef.current = true;
          setHydrated(true);
        } else {
          setMajorTests(current => mergeSelectedWithCatalog(current, nextMajor));
          setMinorTests(current => mergeSelectedWithCatalog(current, nextMinors));
          setBooks(current => mergeSelectedWithCatalog(current, nextBooks));
          setGames(current => mergeSelectedWithCatalog(current, nextGames));
          setEbooks(current => mergeSelectedWithCatalog(current, nextEbooks));
        }
      } catch (error) {
        if (!cancelled) {
          alert.openSimpleAlert();
        }
      } finally {
        if (!cancelled) {
          setLoadingCatalogs(false);
        }
      }
    };

    loadResourceCatalogs();
    return () => {
      cancelled = true;
    };
  }, [consultantId, consultants, currentConsultant, consultantsReady, serviceId, token]);

  const toggleResource = (kind: ResourceKind, item: ResourceItem, checked: boolean) => {
    const setters: Record<ResourceKind, Dispatch<SetStateAction<ResourceItem[]>>> = {
      majorTests: setMajorTests,
      minorTests: setMinorTests,
      books: setBooks,
      games: setGames,
      ebooks: setEbooks,
    };
    const current: Record<ResourceKind, ResourceItem[]> = {
      majorTests,
      minorTests,
      books,
      games,
      ebooks,
    };
    const list = current[kind];
    if (checked) {
      if (list.some(entry => entry.id === item.id)) {
        return;
      }
      setters[kind]([
        ...list,
        {
          ...item,
          status: statusById[item.id] || (kind === 'majorTests' ? 'todo' : item.status),
        },
      ]);
      return;
    }
    setters[kind](list.filter(entry => entry.id !== item.id));
  };

  const removeResource = (kind: ResourceKind, resourceId: number) => {
    const setters: Record<ResourceKind, Dispatch<SetStateAction<ResourceItem[]>>> = {
      majorTests: setMajorTests,
      minorTests: setMinorTests,
      books: setBooks,
      games: setGames,
      ebooks: setEbooks,
    };
    const current: Record<ResourceKind, ResourceItem[]> = {
      majorTests,
      minorTests,
      books,
      games,
      ebooks,
    };
    setters[kind](current[kind].filter(entry => entry.id !== resourceId));
  };

  const handleConsultantChange = (nextId: number) => {
    setConsultantId(nextId);
    setEditingConsultant(false);
  };

  const handleSubmit = async () => {
    if (!user || !serviceId) {
      return;
    }
    if (!consultantId) {
      alert.setOpenAlert(true, CONSULTANT_REQUIRED, 'error');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        service: { id: Number(serviceId) },
        upper_hierarchy: { id: Number(consultantId) },
        service_resources_map: {
          [String(serviceId)]: {
            upper_hierarchy: Number(consultantId),
            tests_to_do: minorTests.map(item => Number(item.id)),
            book_template_ids: books.map(item => Number(item.id)),
            major_test_ids: majorTests.map(item => Number(item.id)),
            authorized_games: games.map(item => Number(item.id)),
            authorized_ebooks: ebooks.map(item => Number(item.id)),
          },
        },
      };
      await apiServiceNative.put(`/api/company-user-admin-edit-service/${user.id}`, payload);
      snapshotRef.current = serializeSelection(
        consultantId,
        majorTests,
        minorTests,
        books,
        games,
        ebooks
      );
      allowLeaveRef.current = true;
      alert.setOpenAlert(true, SAVE_SUCCESS, 'success');
      history.push(beneficiaryProfilePath);
    } catch (error) {
      alert.openSimpleAlert();
    } finally {
      setSubmitting(false);
    }
  };

  const askDelete = () => {
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    if (deleting) {
      return;
    }
    setDeleteOpen(false);
  };

  const confirmDelete = async () => {
    if (!user || !serviceId) {
      return;
    }
    setDeleting(true);
    try {
      await apiServiceNative.put(`/api/company-user-admin-remove-service/${user.id}`, {
        service: { id: Number(serviceId) },
      });
      allowLeaveRef.current = true;
      setDeleteOpen(false);
      alert.setOpenAlert(true, DELETE_PRESTATION_SUCCESS, 'success');
      history.push(`/admin/users-resultats/beneficiaire/${id}?tab=prestations`);
    } catch (error) {
      alert.openSimpleAlert();
    } finally {
      setDeleting(false);
    }
  };

  return {
    loading,
    loadingCatalogs,
    submitting,
    hydrated,
    dirty,
    leaveOpen,
    leaveTitle: LEAVE_TITLE,
    leaveText: LEAVE_TEXT,
    leaveConfirm: LEAVE_CONFIRM,
    deleteOpen,
    deleting,
    deleteTitle: DELETE_PRESTATION_TITLE,
    deleteText: buildDeletePrestationText(prestationName, consultantName),
    deleteConfirm: DELETE_PRESTATION_CONFIRM,
    prestationName,
    consultantName,
    consultantPhoto: selectedConsultant?.photoUri || '',
    consultants: consultantsForService,
    consultantId,
    lockConsultant: isConsultant,
    editingConsultant,
    setEditingConsultant,
    handleConsultantChange,
    majorCatalog,
    minorCatalog,
    bookCatalog,
    gameCatalog,
    ebookCatalog,
    majorTests: applyStatus(majorTests, true),
    minorTests: applyStatus(minorTests, false),
    books,
    games,
    ebooks,
    toggleResource,
    removeResource,
    handleSubmit,
    requestLeave,
    confirmLeave,
    closeLeave: () => setLeaveOpen(false),
    askDelete,
    closeDelete,
    confirmDelete,
    goToBeneficiaries,
  };
};
